import { useEffect, useState } from "react";
import { api, ApiError, createIdempotencyKey } from "../api/client";
import type { ProxyImportResult, SubConnection, SubConnectionInput } from "../api/types";
import { ConfirmDialog } from "./ConfirmDialog";
import { Icon } from "./Icon";
import { Modal } from "./Modal";

interface ConnectionDialogProps {
  open: boolean;
  connection?: SubConnection | null;
  onClose: () => void;
  onSaved: (connection: SubConnection, created: boolean) => void;
}

function validHttpUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function proxyImportErrorResult(reason: unknown): ProxyImportResult | null {
  if (!(reason instanceof ApiError) || !reason.details || typeof reason.details !== "object") return null;
  const payload = reason.details as Record<string, unknown>;
  const detail = payload.detail;
  if (!detail || typeof detail !== "object") return null;
  const invalid = (detail as Record<string, unknown>).invalid;
  if (!Array.isArray(invalid)) return null;
  const rows = invalid.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const row = item as Record<string, unknown>;
    const lineNo = Number(row.line_no);
    if (!Number.isInteger(lineNo) || lineNo < 1 || typeof row.error !== "string") return [];
    return [{ line_no: lineNo, error: row.error }];
  });
  return rows.length ? { imported: 0, invalid: rows } : null;
}

export function ConnectionDialog({ open, connection, onClose, onSaved }: ConnectionDialogProps) {
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [adminKey, setAdminKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(connection?.name || "");
    setBaseUrl(connection?.sub_base_url || "");
    setAdminKey("");
    setBusy(false);
    setDirty(false);
    setDiscardOpen(false);
    setError("");
    setIdempotencyKey("");
  }, [connection?.id, open]);

  function change(setter: (value: string) => void, value: string) {
    setter(value);
    setDirty(true);
    setError("");
    setIdempotencyKey("");
  }

  function requestClose() {
    if (busy) return;
    if (dirty) setDiscardOpen(true);
    else onClose();
  }

  async function submit() {
    if (!name.trim()) {
      setError("请输入连接名称。");
      return;
    }
    if (!validHttpUrl(baseUrl.trim())) {
      setError("请输入以 http:// 或 https:// 开头的有效 Sub2 地址。");
      return;
    }
    if (!connection && !adminKey.trim()) {
      setError("新建连接需要填写管理员密钥。");
      return;
    }
    const key = idempotencyKey || createIdempotencyKey(connection ? "sub2-update" : "sub2-create");
    if (!idempotencyKey) setIdempotencyKey(key);
    setBusy(true);
    setError("");
    try {
      const input: SubConnectionInput = {
        name: name.trim(),
        mode: "direct",
        sub_base_url: baseUrl.trim(),
        admin_key: adminKey.trim(),
      };
      const result = connection
        ? await api.integrations.updateConnection(connection.id, input, key)
        : await api.integrations.createConnection(input, key);
      setDirty(false);
      onSaved(result.connection, !connection);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "连接保存失败，请重试。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open={open && !discardOpen}
        title={connection ? "编辑 Sub2 连接" : "新建 Sub2 连接"}
        eyebrow="SUB2 DESTINATION"
        description="密钥只提交到服务端加密保存，页面不会回显明文。"
        busy={busy}
        onClose={requestClose}
      >
        <div className="integration-form">
          <label className="flow-field"><span>连接名称</span><input data-autofocus value={name} maxLength={120} onChange={(event) => change(setName, event.target.value)} placeholder="例如：东京主节点" /></label>
          <label className="flow-field"><span>Sub2 地址</span><input value={baseUrl} maxLength={1000} inputMode="url" onChange={(event) => change(setBaseUrl, event.target.value)} placeholder="https://sub2.example.test" /></label>
          <label className="flow-field"><span>{connection ? "更新管理员密钥" : "管理员密钥"}</span><input type="password" value={adminKey} maxLength={2000} autoComplete="new-password" onChange={(event) => change(setAdminKey, event.target.value)} placeholder={connection ? `留空则保留 ${connection.admin_key_masked || "现有密钥"}` : "输入 Sub2 管理员密钥"} /></label>
          <div className="inline-notice"><Icon name="key" size={18} /><span>当前只开放后端已实现的 direct 模式；保存后可在列表中执行真实连通性测试。</span></div>
          {error ? <div className="flow-error" role="alert">{error}</div> : null}
        </div>
        <footer className="flow-actions"><button type="button" className="button button--quiet" onClick={requestClose} disabled={busy}>取消</button><button type="button" className="button button--primary" onClick={() => void submit()} disabled={busy}>{busy ? <span className="button-spinner" /> : null}{busy ? "保存中" : connection ? "保存修改" : "创建连接"}</button></footer>
      </Modal>
      <ConfirmDialog open={open && discardOpen} title="丢弃连接修改？" description="尚未保存的地址、名称和密钥输入会被清除。" confirmLabel="丢弃修改" danger onCancel={() => setDiscardOpen(false)} onConfirm={() => { setDirty(false); setDiscardOpen(false); onClose(); }} />
    </>
  );
}

interface ProxyImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: (result: ProxyImportResult, replaced: boolean) => void;
}

export function ProxyImportDialog({ open, onClose, onImported }: ProxyImportDialogProps) {
  const [value, setValue] = useState("");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ProxyImportResult | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const lineCount = value.split(/\r?\n/).filter((line) => line.trim()).length;

  useEffect(() => {
    if (!open) return;
    setValue("");
    setReplace(false);
    setBusy(false);
    setError("");
    setResult(null);
    setDiscardOpen(false);
    setReplaceConfirmOpen(false);
    setIdempotencyKey("");
  }, [open]);

  function requestClose() {
    if (busy) return;
    if (value.trim() && !result) setDiscardOpen(true);
    else onClose();
  }

  function requestSubmit() {
    if (!value.trim()) {
      setError("请至少输入一行代理地址。");
      return;
    }
    if (replace) setReplaceConfirmOpen(true);
    else void submit();
  }

  async function submit() {
    const key = idempotencyKey || createIdempotencyKey("proxy-import");
    if (!idempotencyKey) setIdempotencyKey(key);
    setReplaceConfirmOpen(false);
    setBusy(true);
    setError("");
    try {
      const response = await api.integrations.importProxies(value, replace, key);
      setResult(response);
      onImported(response, replace);
    } catch (reason) {
      const failedResult = proxyImportErrorResult(reason);
      if (failedResult) setResult(failedResult);
      else setError(reason instanceof Error ? reason.message : "代理导入失败，请重试。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal open={open && !discardOpen && !replaceConfirmOpen} title="输入代理" eyebrow="PROXY ROUTES" description="一行一个代理地址，提交后仅保存到当前用户的加密代理池。" busy={busy} onClose={requestClose}>
        <div className="integration-form proxy-import-form">
          {result ? <div className={`flow-success flow-success--compact${result.imported ? "" : " flow-success--warning"}`}><span><Icon name={result.imported ? "check" : "alert"} size={25} /></span><h3>{result.imported ? `服务端已导入 ${result.imported} 条代理` : "没有可用代理被写入"}</h3><p>{result.invalid.length ? `${result.invalid.length} 行未写入，请检查下方诊断。` : "所有输入行均已写入，没有跳过记录。"}</p>{result.invalid.length ? <div className="proxy-import-errors">{result.invalid.slice(0, 12).map((item) => <span key={`${item.line_no}-${item.error}`}>第 {item.line_no} 行：{item.error}</span>)}</div> : null}</div> : <>
            <label className="flow-field"><span>代理地址</span><textarea data-autofocus value={value} onChange={(event) => { setValue(event.target.value); setError(""); setIdempotencyKey(""); }} autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={'http://user:password@host:3128\nsocks5://user:password@host:1080'} /></label>
            <div className="proxy-import-meta"><span>{lineCount} 条非空记录</span><span>支持 http、https、socks5</span></div>
            <label className={`check-control check-control--panel${replace ? " is-danger" : ""}`}><input type="checkbox" checked={replace} onChange={(event) => { setReplace(event.target.checked); setIdempotencyKey(""); }} /><span><b>替换现有代理池</b><small>开启后会先停用全部现有代理，再导入这次有效记录。</small></span></label>
          </>}
          {error ? <div className="flow-error" role="alert">{error}</div> : null}
        </div>
        <footer className="flow-actions">{result ? <>{result.imported ? null : <button type="button" className="button button--quiet" onClick={() => { setResult(null); setIdempotencyKey(""); }}>返回修改</button>}<button type="button" className="button button--primary" onClick={onClose}>{result.imported ? "完成" : "暂时关闭"}</button></> : <><button type="button" className="button button--quiet" onClick={requestClose} disabled={busy}>取消</button><button type="button" className="button button--primary" onClick={requestSubmit} disabled={busy || !value.trim()}>{busy ? <span className="button-spinner" /> : null}{busy ? "导入中" : `导入 ${lineCount} 条代理`}</button></>}</footer>
      </Modal>
      <ConfirmDialog open={open && discardOpen} title="丢弃代理输入？" description="尚未提交的代理地址会从当前页面清除。" confirmLabel="丢弃输入" danger onCancel={() => setDiscardOpen(false)} onConfirm={onClose} />
      <ConfirmDialog open={open && replaceConfirmOpen} title="替换整个代理池？" description="现有活动代理会全部停用，只有本次成功解析的新代理会保留。这个操作不能在页面中撤销。" confirmLabel="确认替换并导入" danger busy={busy} onCancel={() => setReplaceConfirmOpen(false)} onConfirm={() => void submit()} />
    </>
  );
}
